import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonTable from '../../../../common/table/ComonTable';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import SearchBar from '../../../../common/formfields/SearchBar';
import DeleteModal from '../../../../common/commonmodal/DeleteModal';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const UnitList = () => {
    const navigate = useNavigate();
    const { user: authUser } = useSelector((state) => state.auth);
    const [currentPage, setCurrentPage] = useState(1);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const fetchUnits = async () => {
        setLoading(true);
        try {
            const currentSiteId = authUser?.siteId;
            const response = await apiServices({
                method: "GET",
                url: `UnitMaster/GetAllUnitMaster?siteId=${currentSiteId}&pageNumber=${currentPage}&pageSize=${pageInfo.pageSize}`
            });
            if (response.isSuccess || response.success || response.IsSuccess) {
                let apiData = response.data.data;

                // Group by UnitName if data is flat, or use as-is if already grouped
                const groupedMap = new Map();
                apiData.forEach(item => {
                    const name = item.UnitName || item.unitName;

                    // If the item already has a units array, we just use it directly
                    if (item.units && Array.isArray(item.units)) {
                        groupedMap.set(name, item);
                        return;
                    }

                    if (!groupedMap.has(name)) {
                        groupedMap.set(name, {
                            ...item,
                            units: [{
                                UnitNumber: item.UnitNumber || item.unitNumber,
                                WingName: item.WingName || item.wingName,
                                FloorName: item.FloorName || item.floorName,
                                UnitId: item.Id || item.UnitId
                            }]
                        });
                    } else {
                        const existing = groupedMap.get(name);
                        existing.units.push({
                            UnitNumber: item.UnitNumber || item.unitNumber,
                            WingName: item.WingName || item.wingName,
                            FloorName: item.FloorName || item.floorName,
                            UnitId: item.Id || item.UnitId
                        });
                    }
                });

                const groupedData = Array.from(groupedMap.values());

                const total = response?.data?.TotalCount;

                setUnits(groupedData);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: total,
                    totalPages: Math.ceil(total / prev.pageSize) || 1
                }));
            }
        } catch (error) {
            console.error("Error fetching units:", error);
            toast.error("Failed to fetch units.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUnits();
    }, [currentPage, pageInfo.pageSize]);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "UNIT NUMBER", key: "UnitNumber" },
        { label: "UNIT", key: "UnitName" },
        { label: "WING", key: "WingName" },
        { label: "FLOOR", key: "FloorName" },
        { label: "SITE NAME", key: "SiteName" },
        { label: "STATUS", key: "IsActive" },
        { label: "ACTIONS", key: "actions" }
    ];

    const renderCell = (key, row, index) => {
        const val = (k) => row[k] || row[k.charAt(0).toLowerCase() + k.slice(1)] || "—";
        switch (key) {
            case "no":
                return <span className="text-xs font-semibold text-gray-800 dark:text-textprimary">{index + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;
            case "UnitName":
                const isStrict = row.IsStaffApprovalRequired || row.isStaffApprovalRequired;
                return (
                    <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-800 dark:text-textprimary">{val(key)}</span>
                        {isStrict && (
                            <span className="text-[10px] text-orange-600 font-semibold bg-orange-50 dark:bg-orange-900/30 dark:text-orange-400 px-2 py-0.5 rounded-full w-fit mt-1 border border-orange-200 dark:border-orange-800/50" title="Strict Staff Approval Required">
                                Approval Required
                            </span>
                        )}
                    </div>
                );
            case "UnitNumber":
                const unitNumbers = row.units ? row.units.map(u => u.UnitNumber).filter(Boolean).join(', ') : val(key);
                return <span className="text-xs font-semibold text-gray-800 dark:text-textprimary truncate max-w-[200px] inline-block" title={unitNumbers}>{unitNumbers || "—"}</span>;
            case "WingName":
                const wings = row.units ? [...new Set(row.units.map(u => u.WingName).filter(Boolean))].join(', ') : val(key);
                return <span className="text-xs text-[#64748B] dark:text-textsecondary truncate max-w-[120px] inline-block" title={wings}>{wings || "—"}</span>;
            case "FloorName":
                const floors = row.units ? [...new Set(row.units.map(u => u.FloorName).filter(Boolean))].join(', ') : val(key);
                return <span className="text-xs text-[#64748B] dark:text-textsecondary truncate max-w-[120px] inline-block" title={floors}>{floors || "—"}</span>;
            case "SiteName":
                return <span className="text-xs text-[#64748B] dark:text-textsecondary">{val(key)}</span>;
            case "IsActive":
                const status = row.IsActive || row.isActive || 'Active';
                let statusColor = "bg-gray-100 text-gray-600";
                if (status === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (status === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {status}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-xs font-normal text-[#64748B] dark:text-textsecondary">{row[key] || "—"}</span>;
        }
    };

    const handleDeleteClick = (row) => {
        setRecordToDelete(row);
        setIsDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!recordToDelete) return;
        const idToDelete = recordToDelete.UnitId || recordToDelete.unitId || recordToDelete.Id || recordToDelete.id;
        try {
            const response = await apiServices({
                method: "DELETE",
                url: `/UnitMaster/deleteUnit/${idToDelete}`
            });
            if (response.success || response.IsSuccess || response.isSuccess) {
                toast.success("Unit deleted successfully.");
                fetchUnits();
            } else {
                toast.error(response.message || "Failed to delete unit.");
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("An error occurred during deletion.");
        } finally {
            setIsDeleteOpen(false);
            setRecordToDelete(null);
        }
    };

    const filteredData = useMemo(() => {
        let data = units;
        if (statusFilter) {
            data = data.filter(item => item.IsActive === statusFilter);
        }
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            data = data.filter(item =>
                (item.UnitName || "").toLowerCase().includes(query) ||
                (String(item.UnitNumber) || "").toLowerCase().includes(query) ||
                (item.FloorNumber || "").toLowerCase().includes(query) ||
                (item.WingName || "").toLowerCase().includes(query) ||
                (item.SiteName || "").toLowerCase().includes(query)
            );
        }
        return data;
    }, [units, statusFilter, searchQuery]);

    const paginatedData = useMemo(() => {
        return filteredData;
    }, [filteredData]);

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">

            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-bordercolor rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 dark:text-textprimary bg-white dark:bg-actionitembg transition-colors"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="flex flex-col flex-1 gap-4 pt-4">
            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    tableHeading="Units List"
                    headerAction={headerAction}
                    headers={tableHeaders}
                    data={paginatedData}
                    renderCell={(key, row) => renderCell(key, row, paginatedData.indexOf(row))}
                    pagination={{
                        ...pageInfo,
                        pageNumber: currentPage
                    }}
                    onPageChange={setCurrentPage}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    showActionCol={true}
                    onEdit={(row) => navigate(`/zentcomm/site-management/unit/form`, { state: { id: row.UnitId || row.Id } })}
                    onDelete={handleDeleteClick}
                />
            </div>

            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => {
                    setIsDeleteOpen(false);
                    setRecordToDelete(null);
                }}
                onConfirm={confirmDelete}
                description={`Are you sure you want to delete the unit "${recordToDelete?.UnitName || recordToDelete?.UnitNumber}"?`}
            />
        </div>
    );
};

export default UnitList;
